import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import AppsIcon from "@mui/icons-material/Apps";
import GridViewIcon from "@mui/icons-material/GridView";
import GroupIcon from "@mui/icons-material/Group";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";

import type { App, Client } from "../types";
import { MONO } from "../theme";
import { EmptyState, PageHeader } from "../components/ui";
import { AppDetail } from "./AppDetail";
import { DeployDialog } from "./DeployDialog";

export function appHref(app: App, appDomain: string) {
  if (app.routingMode === "subdomain" && appDomain) {
    return `https://${app.name}.${appDomain}/`;
  }
  return `/${app.name}/`;
}

function SummaryMetric({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 130 }}>
      <Box sx={{ color: "primary.main", display: "grid" }}>{icon}</Box>
      <Box>
        <Typography variant="h5" component="p" sx={{ lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  );
}

function AppCard({
  app,
  appDomain,
  onManage,
}: {
  app: App;
  appDomain: string;
  onManage: () => void;
}) {
  const initial = (app.name[0] || "?").toUpperCase();
  const tintIndex = Array.from(app.name).reduce((total, character) => {
    return (total + character.charCodeAt(0)) % 5;
  }, 0);
  const metadata = [
    { label: "Team", value: app.ownerGroup || "Unassigned" },
    { label: "Route", value: app.routingMode || "path" },
  ];

  return (
    <Paper
      elevation={1}
      sx={(theme) => {
        const lightTints = [
          "linear-gradient(145deg, rgba(218, 242, 230, 0.78), rgba(251, 253, 252, 0.64) 66%)",
          "linear-gradient(145deg, rgba(220, 234, 247, 0.76), rgba(251, 253, 252, 0.64) 66%)",
          "linear-gradient(145deg, rgba(234, 225, 246, 0.74), rgba(251, 253, 252, 0.64) 66%)",
          "linear-gradient(145deg, rgba(247, 232, 205, 0.76), rgba(251, 253, 252, 0.64) 66%)",
          "linear-gradient(145deg, rgba(215, 241, 238, 0.76), rgba(251, 253, 252, 0.64) 66%)",
        ];
        const darkTints = [
          "linear-gradient(145deg, rgba(42, 80, 60, 0.48), rgba(21, 29, 24, 0.74) 68%)",
          "linear-gradient(145deg, rgba(42, 63, 81, 0.46), rgba(21, 29, 24, 0.74) 68%)",
          "linear-gradient(145deg, rgba(62, 49, 81, 0.44), rgba(21, 29, 24, 0.74) 68%)",
          "linear-gradient(145deg, rgba(78, 61, 36, 0.43), rgba(21, 29, 24, 0.74) 68%)",
          "linear-gradient(145deg, rgba(35, 76, 72, 0.47), rgba(21, 29, 24, 0.74) 68%)",
        ];

        return {
          p: 2.5,
          height: "100%",
          overflow: "hidden",
          backgroundImage:
            theme.palette.mode === "dark" ? darkTints[tintIndex] : lightTints[tintIndex],
          transition: "border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",
          "&:hover": {
            borderColor: "primary.light",
            boxShadow:
              theme.palette.mode === "dark"
                ? "inset 0 1px 0 rgba(225, 247, 235, 0.1), 0 18px 38px rgba(1, 6, 3, 0.34)"
                : "inset 0 1px 0 rgba(255, 255, 255, 0.82), 0 18px 38px rgba(41, 61, 51, 0.12)",
            transform: "translateY(-2px)",
          },
        };
      }}
    >
      <Stack spacing={2.25} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 1.25,
              bgcolor: (theme) =>
                alpha(
                  theme.palette.background.paper,
                  theme.palette.mode === "dark" ? 0.58 : 0.74,
                ),
              color: "primary.dark",
              border: 1,
              borderColor: (theme) =>
                alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.1 : 0.74),
              boxShadow: (theme) =>
                `inset 0 1px 0 ${alpha(
                  theme.palette.common.white,
                  theme.palette.mode === "dark" ? 0.06 : 0.8,
                )}, 0 8px 18px ${alpha(
                  theme.palette.common.black,
                  theme.palette.mode === "dark" ? 0.18 : 0.06,
                )}`,
              backdropFilter: "blur(10px)",
              display: "grid",
              placeItems: "center",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {initial}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {app.name}
            </Typography>
            <Typography
              sx={{ fontSize: 12, fontFamily: MONO, color: "text.secondary", mt: 0.35 }}
            >
              Version {app.version}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 2,
            py: 0.5,
          }}
        >
          {metadata.map((item) => (
            <Box key={item.label} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{ display: "block", color: "text.secondary", mb: 0.25 }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  textTransform: item.label === "Route" ? "capitalize" : undefined,
                }}
                noWrap
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: "auto", pt: 1.5, borderTop: 1, borderColor: "divider" }}
        >
          <Link
            href={appHref(app, appDomain)}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Open app
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>
          <Button size="small" variant="text" color="inherit" onClick={onManage}>
            Manage
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function CatalogLoading() {
  return (
    <Box
      aria-label="Loading apps"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(3, minmax(0, 1fr))",
        },
        gap: 2.5,
      }}
    >
      {[0, 1, 2].map((item) => (
        <Paper key={item} elevation={1} sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="rounded" width={44} height={44} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton width="55%" />
                <Skeleton width="32%" height={16} />
              </Box>
            </Stack>
            <Skeleton width="45%" height={30} />
            <Skeleton height={42} />
          </Stack>
        </Paper>
      ))}
      <Typography sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
        Loading apps
      </Typography>
    </Box>
  );
}

export function Catalog({ client }: { client: Client }) {
  const [apps, setApps] = useState<App[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<App | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      setApps(await client.listApps());
    } catch (err) {
      setError((err as Error).message);
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  const identity = client.getIdentity && client.getIdentity();
  const canDeployNew =
    !!identity && (identity.isAdmin || identity.groups.some((group) => group !== "atlas-admins"));

  const stats = useMemo(() => {
    const list = apps || [];
    return {
      total: list.length,
      teams: new Set(list.map((app) => app.ownerGroup).filter(Boolean)).size,
      subdomains: list.filter((app) => app.routingMode === "subdomain").length,
    };
  }, [apps]);

  const visibleApps = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return apps || [];
    return (apps || []).filter((app) =>
      `${app.name} ${app.ownerGroup || ""} ${app.routingMode || "path"}`
        .toLowerCase()
        .includes(normalized),
    );
  }, [apps, query]);

  if (selected) {
    return <AppDetail client={client} app={selected} onBack={() => setSelected(null)} />;
  }

  if (deploying) {
    return (
      <DeployDialog
        client={client}
        onClose={() => setDeploying(false)}
        onDeployed={() => void load()}
      />
    );
  }

  return (
    <Stack spacing={3.5}>
      <PageHeader
        title="Apps"
        subtitle="Deploy applications, inspect releases, and manage the services your team owns."
        action={
          canDeployNew && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDeploying(true)}>
              Deploy new app
            </Button>
          )
        }
      />

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Retry
            </Button>
          }
        >
          Failed to load apps: {error}
        </Alert>
      )}

      {apps === null && !error && <CatalogLoading />}

      {apps !== null && (
        <>
          <Paper elevation={1} sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                "& > *": { py: { xs: 1.25, sm: 0 }, px: { sm: 2 } },
                "& > * + *": {
                  borderTop: { xs: `1px solid ${theme.palette.divider}`, sm: 0 },
                  borderLeft: { sm: `1px solid ${theme.palette.divider}` },
                },
              })}
            >
              <Box><SummaryMetric label="Live apps" value={stats.total} icon={<AppsIcon />} /></Box>
              <Box><SummaryMetric label="Owner teams" value={stats.teams} icon={<GroupIcon />} /></Box>
              <Box><SummaryMetric label="Subdomain apps" value={stats.subdomains} icon={<GridViewIcon />} /></Box>
            </Box>
          </Paper>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="h6">Your apps</Typography>
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search apps"
              aria-label="Search apps"
              sx={{ width: { xs: "100%", sm: 280 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          {apps.length === 0 ? (
            <EmptyState
              icon={<AppsIcon />}
              title="No apps yet"
              hint={
                canDeployNew
                  ? "Deploy your first app here or use the Atlas CLI."
                  : "Apps owned by your team will appear here."
              }
              dense
            />
          ) : visibleApps.length === 0 ? (
            <EmptyState
              icon={<SearchIcon />}
              title="No matching apps"
              hint="Try a different app name, owner group, or routing mode."
              dense
            />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2.5,
              }}
            >
              {visibleApps.map((app) => (
                <AppCard
                  key={app.name}
                  app={app}
                  appDomain={client.appDomain}
                  onManage={() => setSelected(app)}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
